/**
 * Fuel Analytics Tab Component
 * Displays comprehensive fuel consumption analytics for all equipment
 * Shows km/lit for vehicles and lit/hr for stationary equipment
 */
import { useState, useEffect, useMemo } from "react";
import { apiClient } from "../../utils/apiClient";
import { 
  Fuel, TrendingUp, TrendingDown, Truck, Settings, 
  Calendar, Filter, BarChart3, Gauge, Clock, Route,
  AlertTriangle, CheckCircle, ChevronDown, ChevronUp,
  History, Download, Eye, X
} from "lucide-react";
import { toast } from "sonner";
import RecalculateReadingsButton from "./RecalculateReadingsButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const FuelAnalyticsTab = ({ projectId, isMasterAdmin }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30"); // 7, 30, 90, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [sortBy, setSortBy] = useState("fuel"); // fuel, efficiency, name
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterType, setFilterType] = useState("all"); // all, vehicles, stationary
  const [expandedEquipment, setExpandedEquipment] = useState(null);
  
  // Fuel History Dialog State
  const [fuelHistoryDialogOpen, setFuelHistoryDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [fuelHistory, setFuelHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [projectId, dateRange, customStartDate, customEndDate]);

  const fetchAnalytics = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      let startDate = "";
      let endDate = new Date().toISOString().split("T")[0];
      
      if (dateRange === "7") {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        startDate = d.toISOString().split("T")[0];
      } else if (dateRange === "30") {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        startDate = d.toISOString().split("T")[0];
      } else if (dateRange === "90") {
        const d = new Date();
        d.setDate(d.getDate() - 90);
        startDate = d.toISOString().split("T")[0];
      } else if (dateRange === "custom" && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else if (dateRange === "all") {
        startDate = "";
        endDate = "";
      }
      
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      
      const response = await apiClient.get(
        `/equipment/fuel-analytics/${projectId}?${params.toString()}`
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch fuel analytics:", error);
      toast.error("Failed to load fuel analytics");
    } finally {
      setLoading(false);
    }
  };

  // Fetch fuel history for a specific equipment
  const openFuelHistory = async (equipment) => {
    setSelectedEquipment(equipment);
    setFuelHistoryDialogOpen(true);
    setLoadingHistory(true);
    
    try {
      // Fetch fuel issues for this specific equipment
      const response = await apiClient.get(`/equipment/fuel-issues/${projectId}?equipment_id=${equipment.equipment_id}`);
      setFuelHistory(response.data || []);
    } catch (error) {
      console.error("Failed to fetch fuel history:", error);
      toast.error("Failed to load fuel history");
      setFuelHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Export fuel history to CSV
  const exportFuelHistory = () => {
    if (!selectedEquipment || fuelHistory.length === 0) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += `Fuel History Report - ${selectedEquipment.equipment_name}\n`;
    csvContent += `Registration/ID: ${selectedEquipment.equipment_number}\n`;
    csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;
    csvContent += `S.No,Date,Fuel Type,Quantity (L),Opening Hr,Closing Hr,Opening KM,Closing KM,KM Run,Hours Run,Issued By,Daily Log Linked\n`;
    
    fuelHistory.forEach((issue, idx) => {
      csvContent += `${idx + 1},"${issue.date}","${issue.fuel_type || 'Diesel'}",${issue.quantity?.toFixed(2) || 0},${issue.opening_hour_reading || '-'},${issue.closing_hour_reading || '-'},${issue.opening_km_reading || '-'},${issue.closing_km_reading || '-'},${issue.km_run ? issue.km_run.toFixed(1) : '-'},${issue.hours_used ? issue.hours_used.toFixed(1) : '-'},"${issue.issued_by_name || issue.received_by || '-'}","${issue.daily_log_linked ? 'Yes' : 'No'}"\n`;
    });
    
    const totalFuel = fuelHistory.reduce((sum, o) => sum + (o.quantity || 0), 0);
    const totalKm = fuelHistory.reduce((sum, o) => sum + (o.km_run || 0), 0);
    const totalHrs = fuelHistory.reduce((sum, o) => sum + (o.hours_used || 0), 0);
    csvContent += `\nTotal,,,"${totalFuel.toFixed(2)} L",,,,,"${totalKm.toFixed(1)} km","${totalHrs.toFixed(1)} hrs"\n`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Fuel_History_${selectedEquipment.equipment_name?.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Fuel history exported");
  };

  // Filter and sort equipment
  const filteredAndSortedEquipment = useMemo(() => {
    if (!analytics?.equipment_analytics) return [];
    
    let filtered = analytics.equipment_analytics;
    
    // Apply type filter
    if (filterType === "vehicles") {
      filtered = filtered.filter(eq => eq.is_vehicle);
    } else if (filterType === "stationary") {
      filtered = filtered.filter(eq => !eq.is_vehicle);
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      if (sortBy === "fuel") {
        comparison = a.total_fuel_consumed - b.total_fuel_consumed;
      } else if (sortBy === "efficiency") {
        comparison = a.avg_consumption - b.avg_consumption;
      } else if (sortBy === "name") {
        comparison = a.equipment_name.localeCompare(b.equipment_name);
      }
      return sortOrder === "desc" ? -comparison : comparison;
    });
    
    return filtered;
  }, [analytics, filterType, sortBy, sortOrder]);

  const getEfficiencyStatus = (equipment) => {
    // Simple heuristic: compare current efficiency to expected
    // For vehicles: higher km/lit is better
    // For stationary: lower lit/hr is better
    if (equipment.avg_consumption === 0) return "neutral";
    
    if (equipment.consumption_unit === "km/lit") {
      return equipment.avg_consumption >= 3 ? "good" : equipment.avg_consumption >= 2 ? "neutral" : "poor";
    } else {
      return equipment.avg_consumption <= 5 ? "good" : equipment.avg_consumption <= 10 ? "neutral" : "poor";
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "k";
    }
    return num.toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20 text-slate-500">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>No fuel analytics data available</p>
      </div>
    );
  }

  const { summary } = analytics;

  return (
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="font-heading font-bold text-xl text-slate-800">
          Fuel Consumption Analytics
        </h3>
        
        <div className="flex flex-wrap items-center gap-3">
          {isMasterAdmin && <RecalculateReadingsButton onDone={fetchAnalytics} />}
          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm"
              />
              <span className="text-slate-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-sm text-sm"
              />
            </div>
          )}
          
          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Equipment</option>
              <option value="vehicles">Vehicles Only</option>
              <option value="stationary">Stationary Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="stat-card bg-gradient-to-br from-orange-50 to-orange-100 border-l-4 border-l-orange-500">
          <div className="flex items-center gap-2 mb-2">
            <Fuel className="w-5 h-5 text-orange-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Total Fuel</span>
          </div>
          <p className="font-heading font-bold text-2xl text-orange-700">
            {formatNumber(summary.total_fuel_consumed)} <span className="text-sm font-normal">lit</span>
          </p>
        </div>
        
        <div className="stat-card bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-l-blue-500">
          <div className="flex items-center gap-2 mb-2">
            <Route className="w-5 h-5 text-blue-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Total KM</span>
          </div>
          <p className="font-heading font-bold text-2xl text-blue-700">
            {formatNumber(summary.total_km_run)} <span className="text-sm font-normal">km</span>
          </p>
        </div>
        
        <div className="stat-card bg-gradient-to-br from-purple-50 to-purple-100 border-l-4 border-l-purple-500">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-5 h-5 text-purple-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Total Hours</span>
          </div>
          <p className="font-heading font-bold text-2xl text-purple-700">
            {formatNumber(summary.total_hours_used)} <span className="text-sm font-normal">hrs</span>
          </p>
        </div>
        
        <div className="stat-card bg-gradient-to-br from-green-50 to-green-100 border-l-4 border-l-green-500">
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-5 h-5 text-green-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Vehicles Avg</span>
          </div>
          <p className="font-heading font-bold text-2xl text-green-700">
            {summary.avg_vehicle_efficiency} <span className="text-sm font-normal">km/lit</span>
          </p>
        </div>
        
        <div className="stat-card bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-l-amber-500">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-5 h-5 text-amber-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Equipment Avg</span>
          </div>
          <p className="font-heading font-bold text-2xl text-amber-700">
            {summary.avg_stationary_efficiency} <span className="text-sm font-normal">lit/hr</span>
          </p>
        </div>
        
        <div className="stat-card bg-gradient-to-br from-slate-50 to-slate-100 border-l-4 border-l-slate-500">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-5 h-5 text-slate-600" />
            <span className="text-xs uppercase tracking-widest text-slate-600 font-medium">Total Equipment</span>
          </div>
          <p className="font-heading font-bold text-2xl text-slate-700">
            {summary.total_equipment}
          </p>
          <p className="text-xs text-slate-500">{summary.total_vehicles} vehicles, {summary.total_stationary} stationary</p>
        </div>
      </div>

      {/* Equipment Analytics Table */}
      <div className="bg-white rounded-sm border shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-slate-50 flex items-center justify-between">
          <h4 className="font-heading font-semibold text-slate-800">Equipment-wise Fuel Consumption</h4>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Sort by:</span>
            <button
              onClick={() => { setSortBy("fuel"); setSortOrder(sortOrder === "desc" ? "asc" : "desc"); }}
              className={`px-2 py-1 rounded ${sortBy === "fuel" ? "bg-primary text-white" : "bg-slate-200 text-slate-700"}`}
            >
              Fuel {sortBy === "fuel" && (sortOrder === "desc" ? "↓" : "↑")}
            </button>
            <button
              onClick={() => { setSortBy("efficiency"); setSortOrder(sortOrder === "desc" ? "asc" : "desc"); }}
              className={`px-2 py-1 rounded ${sortBy === "efficiency" ? "bg-primary text-white" : "bg-slate-200 text-slate-700"}`}
            >
              Efficiency {sortBy === "efficiency" && (sortOrder === "desc" ? "↓" : "↑")}
            </button>
            <button
              onClick={() => { setSortBy("name"); setSortOrder(sortOrder === "desc" ? "asc" : "desc"); }}
              className={`px-2 py-1 rounded ${sortBy === "name" ? "bg-primary text-white" : "bg-slate-200 text-slate-700"}`}
            >
              Name {sortBy === "name" && (sortOrder === "desc" ? "↓" : "↑")}
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Equipment</th>
                <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Type</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Total Fuel</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">KM Run</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Hours Used</th>
                <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Avg Consumption</th>
                <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Fuelings</th>
                <th className="text-center py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredAndSortedEquipment.map((eq) => {
                const efficiencyStatus = getEfficiencyStatus(eq);
                
                return (
                  <tr 
                    key={eq.equipment_id} 
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                    onClick={() => openFuelHistory(eq)}
                    data-testid={`fuel-analytics-row-${eq.equipment_id}`}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-primary" />
                        <div>
                          <p className="font-medium text-slate-800">{eq.equipment_name}</p>
                          <p className="text-xs text-slate-500">{eq.equipment_number}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        eq.is_vehicle 
                          ? "bg-blue-100 text-blue-700" 
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {eq.is_vehicle ? <Truck className="w-3 h-3" /> : <Settings className="w-3 h-3" />}
                        {eq.equipment_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      <span className="font-semibold text-orange-600">{eq.total_fuel_consumed.toFixed(1)}</span>
                      <span className="text-xs text-slate-500 ml-1">lit</span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {eq.total_km_run > 0 ? (
                        <>
                          <span className="font-semibold text-blue-600">{eq.total_km_run.toFixed(1)}</span>
                          <span className="text-xs text-slate-500 ml-1">km</span>
                        </>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono">
                      {eq.total_hours_used > 0 ? (
                        <>
                          <span className="font-semibold text-purple-600" data-testid={`analytics-hours-${eq.equipment_id}`}>{eq.total_hours_used.toFixed(1)}</span>
                          <span className="text-xs text-slate-500 ml-1">hrs</span>
                        </>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Gauge className={`w-4 h-4 ${
                          efficiencyStatus === "good" ? "text-green-500" :
                          efficiencyStatus === "poor" ? "text-red-500" : "text-amber-500"
                        }`} />
                        <span className={`font-bold ${
                          efficiencyStatus === "good" ? "text-green-600" :
                          efficiencyStatus === "poor" ? "text-red-600" : "text-amber-600"
                        }`}>
                          {eq.avg_consumption}
                        </span>
                        <span className="text-xs text-slate-500">{eq.consumption_unit}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        eq.status === "Available" ? "bg-green-100 text-green-700" :
                        eq.status === "In Use" ? "bg-blue-100 text-blue-700" :
                        eq.status === "Under Maintenance" ? "bg-amber-100 text-amber-700" :
                        "bg-red-100 text-red-700"
                      }`}>
                        {eq.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-700 font-semibold text-sm">
                        {eq.fueling_count}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); openFuelHistory(eq); }}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary hover:bg-primary/20 rounded text-sm font-medium transition-colors"
                        title="View Complete Fuel History"
                      >
                        <History className="w-4 h-4" />
                        <span>History</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredAndSortedEquipment.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No equipment data found for the selected filters</p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 text-sm text-slate-600 bg-slate-50 p-4 rounded-sm">
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-green-500" />
          <span>Good efficiency</span>
        </div>
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-amber-500" />
          <span>Average efficiency</span>
        </div>
        <div className="flex items-center gap-2">
          <Gauge className="w-4 h-4 text-red-500" />
          <span>Poor efficiency (needs attention)</span>
        </div>
        <div className="ml-auto text-xs text-slate-500">
          Vehicles: Higher km/lit is better | Equipment: Lower lit/hr is better
        </div>
      </div>

      {/* Fuel History Dialog */}
      <Dialog open={fuelHistoryDialogOpen} onOpenChange={setFuelHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <History className="w-5 h-5" />
              FUEL HISTORY - {selectedEquipment?.equipment_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedEquipment && (
            <div className="space-y-6 mt-4">
              {/* Equipment Info Card */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-sm border border-amber-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Equipment</p>
                    <p className="font-semibold">{selectedEquipment.equipment_name}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Registration / ID</p>
                    <p className="font-mono font-semibold">{selectedEquipment.equipment_number || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Type</p>
                    <p className="font-medium">{selectedEquipment.equipment_type || 'Equipment'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-amber-700 mb-1">Category</p>
                    <p className="font-medium">{selectedEquipment.is_vehicle ? 'Vehicle' : 'Stationary'}</p>
                  </div>
                </div>
              </div>

              {/* Fuel Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-4 bg-orange-100 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-orange-700 mb-1">Total Fuel</p>
                  <p className="font-mono font-bold text-2xl text-orange-700">{selectedEquipment.total_fuel_consumed?.toFixed(1) || 0} L</p>
                </div>
                <div className="text-center p-4 bg-blue-100 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-blue-700 mb-1">Total KM</p>
                  <p className="font-mono font-bold text-2xl text-blue-700" data-testid="detail-total-km">{selectedEquipment.total_km_run?.toFixed(1) || 0} km</p>
                </div>
                <div className="text-center p-4 bg-indigo-100 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-indigo-700 mb-1">Total Hours</p>
                  <p className="font-mono font-bold text-2xl text-indigo-700" data-testid="detail-total-hours">{selectedEquipment.total_hours_used?.toFixed(1) || 0} hrs</p>
                </div>
                <div className="text-center p-4 bg-green-100 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-green-700 mb-1">Avg Consumption</p>
                  <p className="font-mono font-bold text-2xl text-green-700">
                    {selectedEquipment.avg_consumption > 0 
                      ? `${selectedEquipment.avg_consumption} ${selectedEquipment.consumption_unit}`
                      : <span className="text-slate-400 text-lg">No data</span>
                    }
                  </p>
                </div>
                <div className="text-center p-4 bg-purple-100 rounded-sm">
                  <p className="text-xs uppercase tracking-widest text-purple-700 mb-1">Total Fuelings</p>
                  <p className="font-mono font-bold text-2xl text-purple-700">{selectedEquipment.fueling_count || fuelHistory.length}</p>
                </div>
              </div>

              {/* Detailed History Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-heading font-bold text-lg text-slate-800 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Complete Fueling History
                  </h4>
                  {fuelHistory.length > 0 && (
                    <button
                      onClick={exportFuelHistory}
                      className="btn-outline flex items-center gap-2 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Export CSV
                    </button>
                  )}
                </div>
                
                {loadingHistory ? (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                    <p className="text-slate-500 mt-2">Loading history...</p>
                  </div>
                ) : fuelHistory.length > 0 ? (
                  <div className="overflow-x-auto border rounded-sm">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-100">
                          <th className="text-left px-3 py-2 font-semibold">S.No</th>
                          <th className="text-left px-3 py-2 font-semibold">Date</th>
                          <th className="text-left px-3 py-2 font-semibold">Fuel Type</th>
                          <th className="text-right px-3 py-2 font-semibold">Quantity (L)</th>
                          <th className="text-right px-3 py-2 font-semibold">Opening Hr</th>
                          <th className="text-right px-3 py-2 font-semibold">Closing Hr</th>
                          <th className="text-right px-3 py-2 font-semibold">Opening KM</th>
                          <th className="text-right px-3 py-2 font-semibold">Closing KM</th>
                          <th className="text-right px-3 py-2 font-semibold">KM Run</th>
                          <th className="text-right px-3 py-2 font-semibold">Hrs Run</th>
                          <th className="text-left px-3 py-2 font-semibold">Issued By</th>
                          <th className="text-center px-3 py-2 font-semibold">Log</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fuelHistory.map((issue, idx) => {
                          const formatReading = (value) => (value ? Number(value).toFixed(value % 1 ? 1 : 0) : '-');
                          
                          return (
                          <tr key={issue.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-mono text-slate-500">{idx + 1}</td>
                            <td className="px-3 py-2 font-mono font-medium">{issue.date}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                                issue.fuel_type === 'Diesel' || issue.fuel_type === 'HSD' 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {issue.fuel_type || 'Diesel'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-bold text-primary">{issue.quantity?.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                              {formatReading(issue.opening_hour_reading)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                              {formatReading(issue.closing_hour_reading)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                              {formatReading(issue.opening_km_reading)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-600">
                              {formatReading(issue.closing_km_reading)}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-blue-600">
                              {issue.km_run ? `${issue.km_run.toFixed(1)} km` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-green-600">
                              {issue.hours_used ? `${issue.hours_used.toFixed(1)}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-slate-600">{issue.issued_by_name || issue.received_by || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              {issue.daily_log_linked ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-green-100 text-green-700" title="Linked to daily log">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-slate-100 font-semibold">
                          <td colSpan="3" className="px-3 py-2 text-right">TOTAL:</td>
                          <td className="px-3 py-2 text-right font-mono text-primary">
                            {fuelHistory.reduce((sum, o) => sum + (o.quantity || 0), 0).toFixed(2)} L
                          </td>
                          <td colSpan="4"></td>
                          <td className="px-3 py-2 text-right font-mono text-blue-600">
                            {`${fuelHistory.reduce((sum, o) => sum + (o.km_run || 0), 0).toFixed(1)} km`}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-green-600">
                            {`${fuelHistory.reduce((sum, o) => sum + (o.hours_used || 0), 0).toFixed(1)} hrs`}
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-sm">
                    <Fuel className="w-12 h-12 mx-auto mb-2 text-slate-400" />
                    <p>No fuel issues recorded for this equipment</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelAnalyticsTab;
